import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, ShieldCheck, ShieldX, RotateCcw, ExternalLink, FileSearch } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import api from '../lib/api';
import { toast } from 'sonner';

const PUBLIC_BASE = process.env.REACT_APP_BACKEND_URL || '';

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso.slice(0, 16);
  }
};

const AttestationsHistoryModal = ({ open, client, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [reasonInput, setReasonInput] = useState({});

  const load = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    try {
      const r = await api.get(`/tenants/${client.id}/attestations`);
      setItems(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => { if (open && client) load(); }, [open, client, load]);

  const handleRevoke = async (cert) => {
    if (!window.confirm(`Révoquer définitivement l'attestation ${cert.id} ?\nElle sera affichée comme "ATTESTATION RÉVOQUÉE" lors de toute vérification publique.`)) return;
    setRevoking(cert.id);
    try {
      await api.post(`/attestations/${cert.id}/revoke`, { reason: reasonInput[cert.id] || '' });
      toast.success('Attestation révoquée');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la révocation');
    } finally {
      setRevoking(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[720px] max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1B3A4B]">
            <FileSearch className="w-5 h-5" />
            Attestations émises — {client?.name}
          </DialogTitle>
          <DialogDescription>
            Liste des attestations de domicile générées pour ce locataire, avec possibilité de révoquer.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-[#1B3A4B]" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <FileSearch className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>Aucune attestation émise pour ce locataire.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="attestations-list">
              {items.map(cert => {
                const verifyUrl = `${PUBLIC_BASE}/verify/attestation/${cert.id}`;
                return (
                  <div
                    key={cert.id}
                    className={`border rounded-lg p-3 ${cert.revoked ? 'border-red-200 bg-red-50/40' : 'border-emerald-200 bg-emerald-50/40'}`}
                    data-testid={`attestation-${cert.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${cert.revoked ? 'bg-red-100' : 'bg-emerald-100'}`}>
                          {cert.revoked
                            ? <ShieldX className="w-5 h-5 text-red-600" />
                            : <ShieldCheck className="w-5 h-5 text-emerald-600" />}
                        </div>
                        <div className="min-w-0">
                          <p className="font-mono text-sm font-semibold text-gray-900">{cert.id}</p>
                          <p className="text-xs text-gray-500">
                            Émise le {formatDateTime(cert.issued_at)} par {cert.issued_by_name || '—'} ({cert.issued_by_role})
                          </p>
                          {cert.revoked && (
                            <div className="mt-1 text-xs text-red-600">
                              <span className="font-medium">Révoquée le {formatDateTime(cert.revoked_at)}</span>
                              {cert.revoked_by_name && <span> par {cert.revoked_by_name}</span>}
                              {cert.revoked_reason && <span className="italic"> — « {cert.revoked_reason} »</span>}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {cert.revoked
                          ? <Badge className="bg-red-100 text-red-700">Révoquée</Badge>
                          : <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>}
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-dashed flex flex-wrap gap-2 items-center justify-between">
                      <a
                        href={verifyUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-[#1B3A4B] hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" /> Page de vérification publique
                      </a>
                      {!cert.revoked && (
                        <div className="flex items-end gap-2 w-full sm:w-auto">
                          <div className="flex-1 sm:w-64">
                            <Textarea
                              rows={1}
                              placeholder="Motif (optionnel)"
                              value={reasonInput[cert.id] || ''}
                              onChange={e => setReasonInput(s => ({ ...s, [cert.id]: e.target.value }))}
                              className="text-xs min-h-[34px]"
                              data-testid={`revoke-reason-${cert.id}`}
                            />
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={revoking === cert.id}
                            onClick={() => handleRevoke(cert)}
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            data-testid={`revoke-btn-${cert.id}`}
                          >
                            {revoking === cert.id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <><RotateCcw className="w-3.5 h-3.5 mr-1" /> Révoquer</>}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AttestationsHistoryModal;
